/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {PartNumberChangesDetailFooterConfigurationModel} from './models/part-number-changes-detail-footer-configuration.model';
import {PartNumberChangesDetailFooterActionsEnum} from '../../models/part-number-changes-detail-footer-actions.enum';
import {PartNumberChangesDetailTrialDisplayConfigurationModel} from './models/pnc-trial/part-number-changes-detail-trial-display-configuration.model';
import {PartNumberChangesDetailTrialDisplayModeEnum} from './models/pnc-trial/part-number-changes-detail-trial-display-mode.enum';
import {InputDisplayModeEnum} from '../../../shared/input/input-display-mode.enum';
import {PncTypeEnum} from '../../models/pnc-type-enum/pnc-type.enum';
import {PartNumberChangesSolutionDisplayConfigurationModel} from './models/part-number-changes-solution-display-configuration.model';

const PNC_OLD_PART_NUMBER_CREDENTIALS_VIEW_CONFIGURATION: PartNumberChangesDetailTrialDisplayConfigurationModel = {
    pncType: PncTypeEnum.UNKNOWN,
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.DETAIL,
    materialInput: {displayMode: InputDisplayModeEnum.VIEW},
    salesVariantInput: {displayMode: InputDisplayModeEnum.VIEW},
    modelCodeInput: {displayMode: InputDisplayModeEnum.VIEW},
    colourInput: {displayMode: InputDisplayModeEnum.VIEW},
    fullLotNumberInput: {displayMode: InputDisplayModeEnum.VIEW},
    quantityInput: {displayMode: InputDisplayModeEnum.VIEW},
    descriptionInput: {displayMode: InputDisplayModeEnum.VIEW},
    spoNumberInput: {displayMode: InputDisplayModeEnum.VIEW}
};

const PNC_OLD_PART_NUMBER_PLANNER_CONFIGURATION: PartNumberChangesDetailTrialDisplayConfigurationModel = {
    pncType: null,
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
    materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    salesVariantInput: {displayMode: InputDisplayModeEnum.VIEW},
    modelCodeInput: {displayMode: InputDisplayModeEnum.VIEW},
    colourInput: {displayMode: InputDisplayModeEnum.VIEW},
    fullLotNumberInput: {displayMode: InputDisplayModeEnum.VIEW},
    quantityInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    descriptionInput: {displayMode: InputDisplayModeEnum.EDIT},
    partNumberChangeConfig: null,
    spoNumberInput: {displayMode: InputDisplayModeEnum.EDIT}
};

const PNC_OLD_PART_NUMBER_AI_CHANGE_CONFIGURATION: PartNumberChangesDetailTrialDisplayConfigurationModel = {
    pncType: null,
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
    materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    salesVariantInput: {displayMode: InputDisplayModeEnum.EDIT},
    modelCodeInput: {displayMode: InputDisplayModeEnum.EDIT},
    colourInput: {displayMode: InputDisplayModeEnum.EDIT},
    fullLotNumberInput: {displayMode: InputDisplayModeEnum.EDIT},
    quantityInput: {displayMode: InputDisplayModeEnum.EDIT},
    descriptionInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    partNumberChangeConfig: null,
    spoNumberInput: {displayMode: InputDisplayModeEnum.EDIT}
};

const PNC_OLD_PART_NUMBER_F2_MISSING_PARTS_CONFIGURATION: PartNumberChangesDetailTrialDisplayConfigurationModel = {
    pncType: null,
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
    materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    salesVariantInput: {displayMode: InputDisplayModeEnum.EDIT},
    modelCodeInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    colourInput: {displayMode: InputDisplayModeEnum.EDIT},
    fullLotNumberInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    quantityInput: {displayMode: InputDisplayModeEnum.EDIT},
    descriptionInput: {displayMode: InputDisplayModeEnum.EDIT},
    partNumberChangeConfig: null,
    spoNumberInput: {displayMode: InputDisplayModeEnum.EDIT}
};

const PNC_OLD_PART_NUMBER_PARTNER_CONFIGURATION: PartNumberChangesDetailTrialDisplayConfigurationModel = {
    pncType: null,
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
    materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    salesVariantInput: {displayMode: InputDisplayModeEnum.EDIT},
    modelCodeInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    colourInput: {displayMode: InputDisplayModeEnum.EDIT},
    fullLotNumberInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    quantityInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    descriptionInput: {displayMode: InputDisplayModeEnum.EDIT},
    partNumberChangeConfig: null,
    spoNumberInput: {displayMode: InputDisplayModeEnum.EDIT}
};

const TECHNICIAN_REJECT_ACCEPT_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Accept',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.ACCEPT,
        class: 'btn-secondary'
    },
    secondaryButton: {
        label: 'Reject',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.REJECT,
        class: 'btn-text-link'
    }
};

const TECHNICIAN_SOLVING_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Send',
        disable: true,
        action: PartNumberChangesDetailFooterActionsEnum.SOLVE,
        class: 'btn-primary'
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE,
        class: 'btn-text-link'
    }
};

const PNC_SEND_DISCARD_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Send',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.SEND_TRIAL,
        class: 'btn-primary'
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_TRIAL,
        class: 'btn-text-link'
    }
};

const PNC_SEND_DISCARD_CANCEL_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Send',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.SEND_TRIAL,
        class: 'btn-primary'
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_TRIAL,
        class: 'btn-text-link'
    },
    thirdButton: {
        label: 'Cancel PNC',
        disable: false,
        action: PartNumberChangesDetailFooterActionsEnum.CANCEL,
        class: 'btn-text-link'
    }
};

const PNC_CREATE_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Create',
        disable: true,
        class: 'btn-primary',
        action: PartNumberChangesDetailFooterActionsEnum.CREATE
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        class: 'btn-text-link',
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_CREATE
    }
};

const PNC_CREATE_AND_SOLVE_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Create and send solution',
        disable: true,
        class: 'btn-primary',
        action: PartNumberChangesDetailFooterActionsEnum.CREATE
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        class: 'btn-text-link',
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_CREATE
    }
};

const PNC_SEND_EE_SPECIALIST_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Send to EE specialist',
        disable: true,
        class: 'btn-primary',
        action: PartNumberChangesDetailFooterActionsEnum.SEND_SOLUTION
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        class: 'btn-text-link',
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_SEND_SOLUTION
    }
};

const EE_SPECIALIST_REJECT_ACCEPT_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Accept',
        disable: true,
        class: 'btn-primary',
        action: PartNumberChangesDetailFooterActionsEnum.ACCEPT_SOLUTION
    },
    secondaryButton: {
        label: 'Reject',
        disable: false,
        class: 'btn-text-link',
        action: PartNumberChangesDetailFooterActionsEnum.REJECT_SOLUTION
    }
};

const EE_SPECIALIST_SOLVING_FOOTER_CONFIGURATION: PartNumberChangesDetailFooterConfigurationModel = {
    primaryButton: {
        label: 'Send',
        disable: true,
        class: 'btn-primary',
        action: PartNumberChangesDetailFooterActionsEnum.SOLVE_AFTER_EE
    },
    secondaryButton: {
        label: 'Discard',
        disable: false,
        class: 'btn-text-link',
        action: PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE_AFTER_EE
    }
};

const PNC_AI_CHANGE_SOLUTION_CONFIGURATION: PartNumberChangesSolutionDisplayConfigurationModel = {
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.CREATE,
    quantityInput: {displayMode: InputDisplayModeEnum.EDIT},
    materialInput: {displayMode: InputDisplayModeEnum.EDIT, required: true},
    additionalInformation: {displayMode: InputDisplayModeEnum.EDIT}
};

const PNC_SOLUTION_TRIALS_CONFIGURATION: PartNumberChangesSolutionDisplayConfigurationModel = {
    displayMode: PartNumberChangesDetailTrialDisplayModeEnum.DETAIL_SOLUTION,
    materialInput: {displayMode: InputDisplayModeEnum.VIEW},
    quantityInput: {displayMode: InputDisplayModeEnum.VIEW},
    additionalInformation: {displayMode: InputDisplayModeEnum.VIEW}
};


const PNC_DISPLAY_CONFIGURATION_CONSTANTS = {
    footer: {
        technicianRejectAccept: TECHNICIAN_REJECT_ACCEPT_FOOTER_CONFIGURATION,
        technicianSolve: TECHNICIAN_SOLVING_FOOTER_CONFIGURATION,
        pncSendTrial: PNC_SEND_DISCARD_FOOTER_CONFIGURATION,
        pncSendOrCancelTrial: PNC_SEND_DISCARD_CANCEL_FOOTER_CONFIGURATION,
        pncCreate: PNC_CREATE_FOOTER_CONFIGURATION,
        pcnCreateAndSolve: PNC_CREATE_AND_SOLVE_FOOTER_CONFIGURATION,
        pncSendToEESpecialist: PNC_SEND_EE_SPECIALIST_FOOTER_CONFIGURATION,
        pncSolvedEESpecialist: EE_SPECIALIST_REJECT_ACCEPT_FOOTER_CONFIGURATION,
        pncSendToTechnician: EE_SPECIALIST_SOLVING_FOOTER_CONFIGURATION
    },
    title: {
        credentials: 'PNC credentials',
        oldPartNumber: 'Old part number credentials',
        solution: 'PNC solution',
        cancellation: 'PNC cancellation',
        dialogHistoryTrials: 'Dialog history',
    },
    solution: {
        aiChange: PNC_AI_CHANGE_SOLUTION_CONFIGURATION,
        solutionTrials: PNC_SOLUTION_TRIALS_CONFIGURATION
    },
    oldPartNumber: {
        view: PNC_OLD_PART_NUMBER_CREDENTIALS_VIEW_CONFIGURATION,
        createPartner: PNC_OLD_PART_NUMBER_PARTNER_CONFIGURATION,
        createPlanner: PNC_OLD_PART_NUMBER_PLANNER_CONFIGURATION,
        createF2MissingParts: PNC_OLD_PART_NUMBER_F2_MISSING_PARTS_CONFIGURATION,
        createAiChange: PNC_OLD_PART_NUMBER_AI_CHANGE_CONFIGURATION
    }
};

export default PNC_DISPLAY_CONFIGURATION_CONSTANTS;
